import * as React from 'react';
import Typography from '@mui/material/Typography';
import InfoIconWithTooltip from 'components/InfoIconWithTooltip';
import ParameterTag from '../PerformanceReview/ParameterTag';

import { Box, Divider } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { SuccessTip } from 'components/PerformanceReview/ParameterwiseFeedback';
import { SubCategoriesDescription } from 'components/PerformanceReview/constants';
import { Break } from 'components/AppShell';
import { snakeCaseToSentence } from 'utils';
import { useNavigateWithClient } from 'hooks';

const useStyles = makeStyles((theme) => ({
  featureContent: {
    width: '100%',
    borderColor: theme.palette.neutral['clr-400'],
    height: '320px',
    borderRadius: '4px',
    padding: theme.spacing(4),
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '8px'
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#F0F5FA',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#BFCAD9',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#8692A3',
    },
    '&::-webkit-scrollbar-thumb:active': {
      backgroundColor: '#636F7F',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  fluencyCatBtn: {
    padding: '4px 8px',
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    border: '2px solid',
    borderRadius: '4px',
    borderColor: theme.palette.neutral['clr-400'],
    cursor: 'pointer',
  },
  selectedCatBtn: {
    borderColor: theme.palette.primary.main,
  },
  fluencyContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'auto'
  }, tipsBox: {
    border: '2px solid',
    borderColor: theme.palette.secondary['clr-600'],
    padding: theme.spacing(2),
    borderRadius: '4px',
  },
  failedContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.danger['clr-100'],
    borderRadius: '4px',
    opacity: 0.8,
    height: '120px',
    justifyContent: 'center',
    alignItems: 'center',
  }
}));

function FluencyAnalysis({ features = [] }) {
  const classes = useStyles();

  const [selectedIndex, setSelectedFeature] = React.useState(0);

  const handleSelect = (index) => {
    setSelectedFeature(index);
  };

  return (
    (features.length) ?
      <>
        {features.length > 1 && (
          <Box mb={4}>
            <Typography variant='body01-bold' color='neutral.clr-600'>
              Click to see why your score was affected
            </Typography>
          </Box>
        )}

        <Box className={classes.fluencyContainer}>
          <Box style={{ display: 'flex', gap: '4px', }}>
            {
              features.map(({ subCategory }, index) => (
                <Box
                  className={`${classes.fluencyCatBtn} ${index === selectedIndex && classes.selectedCatBtn}`}
                  onClick={() => handleSelect(index)}
                  key={index}
                >
                  <Typography
                    variant='body02-bold'
                    color={index === selectedIndex ? 'primary.main' : 'neutral.clr-900'}
                  >
                    {snakeCaseToSentence(subCategory)}
                  </Typography>

                  <InfoIconWithTooltip
                    placement='right'
                    fontSize='14px'
                    title={SubCategoriesDescription[subCategory]}
                  />
                </Box>
              ))
            }
          </Box>

          <ParameterTag tag={features[selectedIndex]} />

        </Box>
      </> :
      <SuccessTip
        message={'Well done! Your speech was quite fluent in this response. Keep practicing to maintain this level. Additionally, you can read the blog below to further enhance your performance!'}
      />

  );
}

function DeliveryAnalysis({ features = [] }) {

  return (
    <Box mb={4} style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
      {
        features.map((feature) => (
          <Box my={1}>
            <ParameterTag tag={feature} />
          </Box>
        ))
      }
    </Box>
  );
}

const Tips = ({ subCategory, score, features }) => {
  const classes = useStyles();
  const navigate = useNavigateWithClient();

  const { tipsData = [] } = React.useMemo(() => {
    let tips = [];
    switch (subCategory) {
      case 'FLUENCY':
        if (features.length) {
          tips.push(
            'Try to minimize above fluency mistakes, as it will help you improve your overall fluency'
          );
        }
        tips.push("Pause briefly at key points to organize your thoughts, but avoid long silences known as breakdown")
        tips.push("Focus on taking a deep breath before responding to calm your nerves and reduce nervousness");
        break;

      case 'DELIVERY':
        features.forEach(feature => {
          switch (feature.subCategory) {
            case 'PACE':
              tips.push("Maintain a steady speaking pace—avoid rushing through your answer or speaking too slowly, to ensure clarity");
              break;
            case 'ENERGY':
              tips.push("Use a clear and energetic voice by breathing properly and sitting with good posture");
              break;
            case 'CONFIDENCE':
              tips.push("Vary your pitch to avoid sounding monotonous and to keep the listener engaged throughout your response");
              break;

            default:
              break;
          }
        });

        break;

      default:
        return { tipsData: [] };;
    }

    return { tipsData: tips };
  }, [subCategory, score, features]);

  return (
    <Box className={classes.tipsBox}>
      <Typography variant='body01-semiBold'>Improvement Tips:-</Typography>
      <ul style={{ margin: '8px 0px 0px 0px', paddingLeft: '30px' }}>
        {
          tipsData.map((tip, i) => {
            return <li key={i}><Typography variant='body01-semiBold'>{tip}</Typography></li>
          })
        }
      </ul>
    </Box>
  )
}

const FailedAnalysis = () => {
  const classes = useStyles();

  return (
    <Box>
      <Box className={classes.failedContainer}>
        <Box width={60}>
          <img
            src='https://assets.languify.in/images/failed-recoding.png'
            alt='failed' width={'100%'}
          />
        </Box>
        <Typography variant='body01-bold' color='danger.clr-700'>
          No explanation found!
        </Typography>
      </Box>

      <Break value={4} />

      <Typography variant='body01-bold' >
        Here are some possible reasons:
      </Typography>

      <ul style={{ margin: '10px 0px 0px 0px', paddingLeft: '25px', }} >
        <li style={{ marginBottom: '4px' }}><Typography variant='body01-semiBold'>
          Microphone not working or muted.
        </Typography></li>
        <li style={{ marginBottom: '4px' }}><Typography variant='body01-semiBold'>
          Didn’t provide explanations to the questions interviewer asked.
        </Typography></li>
        <li style={{ marginBottom: '4px' }}><Typography variant='body01-semiBold'>
          Background noise affected the recording.
        </Typography></li>
        <li style={{ marginBottom: '4px' }}><Typography variant='body01-semiBold'>
          Internet was weak or dropped.
        </Typography></li>
      </ul>

    </Box>
  );
}

export function SpeechAnalysis({ subCategory }) {
  const classes = useStyles();

  const { id, description, features, score, failed } = React.useMemo(() => {
    return {
      id: subCategory?.id,
      description: subCategory?.description,
      features: subCategory?.features || [],
      score: subCategory.score,
      failed: subCategory?.failed
    }
  }, [subCategory]);

  return (

    <Box className={classes.featureContent}>


      <Divider sx={{ margin: '2px 0px 12px 0px', borderColor: '#CCD4DE' }} />

      {
        (!failed) ? <Box>
          {

            ['FLUENCY'].includes(id) && <FluencyAnalysis features={features} />

          }

          {

            ['DELIVERY'].includes(id) && <DeliveryAnalysis features={features} />

          }

          <Break value={2} />
          <Divider sx={{ margin: '8px 0px 12px 0px', borderColor: '#CCD4DE' }} />
          <Tips features={features} subCategory={id} score={score} />
        </Box> :

          <FailedAnalysis />
      }

    </Box>
  );
}
